import debounce from 'lodash/debounce';

import { getSuggestedResults } from '@/features/GlobalSearch/api/search';

import type { SearchResults } from './searchStorage';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { Dispatch, SetStateAction } from 'react';

interface SearchRef {
  assetType: AssetType;
  keyword: string;
  requestId: number;
}

// Function to create a debounced cache updater
export function createUpdateSearchCache(
  setCachedSearchResults: Dispatch<
    SetStateAction<Record<string, SearchResults>>
  >
) {
  return debounce((cacheKey: string, result: SearchResults) => {
    try {
      setCachedSearchResults((prev) => {
        // Aggressively limit cache size
        const updatedCache = { ...prev };
        const keys = Object.keys(updatedCache);

        // If we're approaching the limit, remove oldest entries
        if (keys.length >= 20) {
          // Reduced from 50 to 20
          // Remove the oldest entries
          const keysToRemove = keys.slice(0, Math.max(5, keys.length - 15));

          keysToRemove.forEach((k) => {
            delete updatedCache[k];
          });
        }

        // Add the new entry
        return {
          ...updatedCache,
          [cacheKey]: result,
        };
      });
    } catch (error) {
      console.warn('Error updating search cache:', error);
      // We still have the current results in memory, just couldn't cache
    }
  }, 500); // Debounce writes to localStorage to prevent rapid succession
}

// Function to search by keyword
export function createSearchByKeyword(
  setSearchResults: Dispatch<SetStateAction<SearchResults | undefined>>,
  cachedSearchResults: Record<string, SearchResults>,
  updateSearchCache: ReturnType<typeof createUpdateSearchCache>,
  requestIdCounter: React.MutableRefObject<number>,
  currentSearchRef: React.MutableRefObject<SearchRef>
) {
  return async (keyword: string, assetType: AssetType) => {
    keyword = keyword.trim();

    if (!keyword) {
      setSearchResults(undefined);

      return;
    }

    // Generate a unique request ID
    const requestId = requestIdCounter.current + 1;

    requestIdCounter.current = requestId;

    // Store the current search parameters with request ID
    currentSearchRef.current = {
      keyword,
      assetType,
      requestId,
    };

    // Create a cache key that includes the asset type
    const cacheKey = `${keyword}::${assetType}`;

    // Check if we already have this result cached
    const cachedResult = cachedSearchResults[cacheKey];

    if (cachedResult) {
      // Only update if this is still the active request
      if (currentSearchRef.current.requestId === requestId) {
        setSearchResults(cachedResult);
      }

      return; // Use cached results if available
    }

    // Perform the search
    try {
      const search = await getSuggestedResults(keyword, assetType);

      // Only update results if this is still the latest search request
      if (
        currentSearchRef.current.keyword === keyword &&
        currentSearchRef.current.assetType === assetType &&
        currentSearchRef.current.requestId === requestId
      ) {
        // Limit the number of hits to reduce storage
        const limitedSearch = search.map((category) => {
          if (
            category.hits &&
            Array.isArray(category.hits) &&
            category.hits.length > 10
          ) {
            return {
              ...category,
              hits: category.hits.slice(0, 10),
            };
          }

          return category;
        });

        const result = {
          searchTerm: keyword,
          results: limitedSearch,
          assetType,
        };

        setSearchResults(result);

        // Update the cache in a debounced way to prevent rapid updates
        updateSearchCache(cacheKey, result);
      }
    } catch (error) {
      console.error('Search error:', error);

      // If there was an error, clear results only if this was the latest request
      if (currentSearchRef.current.requestId === requestId) {
        setSearchResults(undefined);
      }
    }
  };
}

// Create a more optimized debounced search function
export function createDebouncedSearch(
  searchByKeyword: ReturnType<typeof createSearchByKeyword>,
  setSearchResults: Dispatch<SetStateAction<SearchResults | undefined>>,
  cachedSearchResults: Record<string, SearchResults>,
  requestIdCounter: React.MutableRefObject<number>,
  currentSearchRef: React.MutableRefObject<SearchRef>
) {
  // Fast debounce for UI updates and cache checks
  const fastDebounceFn = debounce((keyword: string, assetType: AssetType) => {
    if (!keyword.trim()) {
      setSearchResults(undefined);

      return;
    }

    // Check cache first for immediate feedback
    const cacheKey = `${keyword}::${assetType}`;
    const cachedResult = cachedSearchResults[cacheKey];

    if (cachedResult) {
      setSearchResults(cachedResult);
    }
  }, 50); // Very quick response for cache checks

  // Slower debounce for API calls
  const apiDebounceFn = debounce((keyword: string, assetType: AssetType) => {
    if (!keyword.trim()) {
      return;
    }

    // Generate a new request ID for this debounced call
    const requestId = requestIdCounter.current + 1;

    requestIdCounter.current = requestId;

    // Update the search reference with the new request ID
    currentSearchRef.current = {
      keyword,
      assetType,
      requestId,
    };

    // Only search if we don't have cached results
    const cacheKey = `${keyword}::${assetType}`;

    if (!cachedSearchResults[cacheKey]) {
      void searchByKeyword(keyword, assetType).catch(console.error);
    }
  }, 200);

  // Return the debounced functions
  return {
    search: (keyword: string, assetType: AssetType) => {
      // Always run the fast debounce
      fastDebounceFn(keyword, assetType);
      // Also queue the API debounce
      apiDebounceFn(keyword, assetType);
    },
    cancel: () => {
      fastDebounceFn.cancel();
      apiDebounceFn.cancel();
    },
    flush: () => {
      fastDebounceFn.flush();
      apiDebounceFn.flush();
    },
    immediate: (keyword: string, assetType: AssetType) => {
      fastDebounceFn.cancel();
      apiDebounceFn.cancel();

      const requestId = requestIdCounter.current + 1;

      requestIdCounter.current = requestId;

      currentSearchRef.current = {
        keyword,
        assetType,
        requestId,
      };

      if (keyword.trim()) {
        // Check cache first
        const cacheKey = `${keyword}::${assetType}`;
        const cachedResult = cachedSearchResults[cacheKey];

        if (cachedResult) {
          setSearchResults(cachedResult);
        }

        // Then do the API call if needed
        if (!cachedResult) {
          void searchByKeyword(keyword, assetType).catch(console.error);
        }
      } else {
        setSearchResults(undefined);
      }
    },
  };
}
